import { TEDBOAktivitetTypEnum } from '../../../../../../globalQueryTypes';
import {
  IValidateFn,
  IFieldValidations,
} from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { AktivitetQuery_node_TEDBOAktivitet as IAktivitetNode } from '../Queries/_QueryTypes/AktivitetQuery';

// Define validation error types for better type safety
interface IValidationError {
  fieldName: string;
  message: string;
  type: 'Error' | 'Warning';
  path?: string[];
}

// Separate validation functions for different parts of the form
const validateTitle = (currentValues: IAktivitetNode): IValidationError[] => {
  if (!currentValues.NOTERING2 && !currentValues.TITEL) {
    return [
      {
        fieldName: 'TITEL',
        message: 'Titel måste fyllas i',
        type: 'Error',
      },
    ];
  }
  return [];
};

const validateDates = (currentValues: IAktivitetNode): IValidationError[] => {
  if (!currentValues.FRANDATUM || !currentValues.TILLDATUM) {
    return [];
  }

  if (currentValues.FRANDATUM > currentValues.TILLDATUM) {
    return [
      {
        fieldName: 'TILLDATUM',
        message: 'Tilldatum måste vara senare än fråndatum',
        type: 'Error',
      },
    ];
  }
  return [];
};

const createDateTime = (date: string, time: string): Date => {
  return new Date(`${date} ${time}`);
};
const hasResources = (currentValues: IAktivitetNode): boolean => {
  return Boolean(currentValues._Associations?.AktBokRubrik?.length);
};

const validateTimes = (currentValues: IAktivitetNode): IValidationError[] => {
  const errors: IValidationError[] = [];

  if (!currentValues.FRANTID || !currentValues.TILLTID) {
    if (
      currentValues.FRANTID === undefined ||
      currentValues.FRANTID === null ||
      currentValues.FRANTID === ''
    ) {
      errors.push({
        fieldName: 'FRANTID',
        message: 'Frantid måste fyllas i',
        type: 'Error',
      });
    }

    if (
      currentValues.TILLTID === undefined ||
      currentValues.TILLTID === null ||
      currentValues.TILLTID === ''
    ) {
      errors.push({
        fieldName: 'TILLTID',
        message: 'Tilltid måste fyllas i',
        type: 'Error',
      });
    }

    return errors;
  }

  const frantidDate = createDateTime(
    currentValues.FRANDATUM,
    currentValues.FRANTID
  );
  const tilltidDate = createDateTime(
    currentValues.TILLDATUM,
    currentValues.TILLTID
  );

  if (frantidDate >= tilltidDate) {
    errors.push({
      fieldName: 'TILLTID',
      message: 'Tilltid måste vara senare än fråntid',
      type: 'Error',
    });
  }

  // Validate booking times
  currentValues._Associations?.AktBokRubrik?.forEach(rubrik => {
    if (!rubrik.FRANTID || !rubrik.TILLTID) return;

    const rubrikFranTidDate = createDateTime(
      currentValues.FRANDATUM,
      rubrik.FRANTID
    );
    const rubrikTilltidDate = createDateTime(
      currentValues.TILLDATUM,
      rubrik.TILLTID
    );

    if (rubrikFranTidDate >= rubrikTilltidDate) {
      errors.push({
        fieldName: 'TILLTID',
        message: 'Tilltid måste vara senare än fråntid',
        type: 'Error',
        path: ['AktBokRubrik', rubrik.id],
      });
    }
  });
  return errors;
};

const validateExternalPublicering = (
  currentValues: IAktivitetNode
): IValidationError[] => {
  if (!currentValues.SKK_SPRIDNINGSTYP_EXTERN) {
    return [];
  }

  const hasForsamling =
    currentValues._Associations?.Resurs1?._Associations?.Resurs?._Associations
      ?.Forsamling?.ENHETSID;

  const hasBookingGroup =
    currentValues._Associations?.Bokningsgrupp?.SKK_ORG_ID;

  if (!hasForsamling && !hasBookingGroup) {
    return [
      {
        fieldName: 'SKK_SPRIDNINGSTYP_EXTERN',
        message: 'Platskoppling och församling saknas',
        type: 'Warning',
      },
    ];
  }

  return [];
};

const validateResources = (
  currentValues: IAktivitetNode
): IValidationError[] => {
  if (!hasResources(currentValues)) {
    return [
      {
        fieldName: 'PLATS',
        message: 'Inga resurser valda',
        type: 'Error',
      },
    ];
  }
  return [];
};
export const validateMinimumResources: IValidateFn<IAktivitetNode> = ({
  currentValues,
}) => {
  if (!currentValues) {
    return [];
  }

  return validateResources(currentValues);
};

export const validateGudstjanstTyp = (
  sysOptionKravVerksamhetstyp: boolean
): IValidateFn<IAktivitetNode> => {
  return ({ currentValues }) => {
    if (!currentValues) {
      return [];
    }

    if (currentValues._typ === TEDBOAktivitetTypEnum.GUDSTJANST) {
      const gudstjanstTypName =
        currentValues._Associations?.GudstjanstTyp?.NYTT_NAMN;
      if (!gudstjanstTypName || gudstjanstTypName.trim() === '') {
        return [
          {
            fieldName: 'id',
            message: 'Gudstjänsttyp måste fyllas i',
            type: 'Error',
          },
        ];
      }
    } else if (currentValues._typ === TEDBOAktivitetTypEnum.AKTOVRIGT) {
      const verksamhetsTypName =
        currentValues._Associations?.Verksamhetstyp?.BENAMNING;
      if (
        sysOptionKravVerksamhetstyp &&
        (!verksamhetsTypName || verksamhetsTypName.trim() === '')
      ) {
        return [
          {
            fieldName: 'id',
            message: 'Verksamhetstyp måste fyllas i',
            type: 'Error',
          },
        ];
      }
    }
    return [];
  };
};

// Main validation function that combines all validations
export const validateAktivitet: IValidateFn<IAktivitetNode> = ({
  currentValues,
}) => {
  if (!currentValues) {
    return [];
  }

  // Combine all validation results
  const validations: IFieldValidations = [
    ...validateTitle(currentValues),
    ...validateDates(currentValues),
    ...validateTimes(currentValues),
    ...validateExternalPublicering(currentValues),
  ];

  return validations;
};

export default {
  validateAktivitet,
  validateMinimumResources,
  validateGudstjanstTyp,
};
